import type { GuideStepContent } from './phantomContent';

export const NIGHTLY_CONTENT = {
  title: 'Nightly wallet guide',
  lead:
    'Nightly is a multichain browser wallet that works with Solana. Follow these steps to install it, connect to Canadawale Yar, and send or receive CWY.',
  steps: [
    {
      title: 'Get Nightly',
      phase: 'install',
      body: [
        'Open the official Nightly website. You will see Get Nightly Extension for desktop browsers, plus App Store and Google Play for mobile.',
        'Always start from the official Nightly site — do not install from random ads or email links.',
      ],
      link: {
        href: 'https://nightly.app',
        label: 'Open nightly.app',
      },
      image: {
        src: '/wallet_ui/nightly-homepage.jpg',
        alt: 'Nightly homepage with Get Nightly Extension and mobile download buttons',
        caption: 'On the Nightly homepage, click Get Nightly Extension to start installing the browser wallet.',
      },
    },
    {
      title: 'Download the browser extension',
      phase: 'install',
      body: [
        'On the Browser Extensions page, choose your browser: Chrome, Brave, Firefox, or Safari.',
        'Install the extension, then pin Nightly in your browser toolbar so you can open it easily.',
      ],
      image: {
        src: '/wallet_ui/nightly-browser-extensions.jpg',
        alt: 'Nightly browser extensions page showing Chrome, Brave, Firefox, and Safari',
        caption: 'Pick Chrome, Brave, Firefox, or Safari and install Nightly from the official store listing.',
      },
      tips: [
        'Create a new wallet or import an existing one, then set a strong password for this device.',
        'Save your recovery phrase offline. Never share it with anyone.',
      ],
    },
    {
      title: 'Select Nightly on CWY',
      phase: 'connect',
      body: [
        'Sign in to Canadawale Yar and open Profile → Wallet.',
        'Click Connect wallet (or open the wallet menu if another wallet is already linked), then choose Nightly from the list.',
      ],
      image: {
        src: '/wallet_ui/nightly-change-wallet.jpg',
        alt: 'CWY Change wallet panel listing Phantom, Solflare, Backpack, Brave Wallet, and Nightly',
        caption: 'In the Change wallet / Connect list, select Nightly to link it to your CWY account.',
      },
      tips: [
        'If Nightly does not appear, make sure the extension is installed and unlocked, then refresh the page.',
      ],
    },
    {
      title: 'Approve the signature request',
      phase: 'connect',
      body: [
        'Nightly will open a Signature Request from Canadawale Yar (app.canadawaleyar.com).',
        'This message only proves you own the wallet — it does not send money. Review it, then click Approve.',
      ],
      image: {
        src: '/wallet_ui/nightly-signature-request.jpg',
        alt: 'Nightly Signature Request modal verifying CWY wallet ownership for provider nightly',
        caption: 'Click Approve on the Signature Request to finish connecting Nightly to CWY.',
      },
      warnings: [
        'Check that the site is https://app.canadawaleyar.com before you approve.',
      ],
    },
    {
      title: 'Receive CWY',
      phase: 'receive',
      body: [
        'Once Nightly is connected, click Receive on the Wallet page (Request CWY tokens).',
        'Confirm your receiving address shows via Nightly, choose the amount (minimum 1 CWY), then click Request CWY.',
        'Depending on program rules, a request may need approval before CWY is sent to your Nightly wallet.',
      ],
      image: {
        src: '/wallet_ui/nightly-receive-cwy.jpg',
        alt: 'Receive CWY modal showing Nightly wallet address and Request CWY button',
        caption: 'Use Receive to request CWY into your connected Nightly wallet.',
      },
    },
    {
      title: 'Send CWY back to the vault',
      phase: 'send',
      body: [
        'To return CWY to the platform, click Send (Return CWY tokens / Add CWY to Your Account).',
        'Choose how much to deposit (25%, 50%, 75%, or Max), confirm the verified CWY destination address, then click Deposit to CWY.',
      ],
      image: {
        src: '/wallet_ui/nightly-deposit-cwy.jpg',
        alt: 'Add CWY to Your Account modal with Nightly wallet, verified destination, and SOL fee warning',
        caption: 'Deposit CWY from Nightly to the verified CWY vault address. Keep some SOL for network fees.',
      },
      warnings: [
        'Solana needs a small amount of SOL for network fees — CWY alone is not enough. If you see a fee warning, add a little SOL before depositing.',
      ],
    },
    {
      title: 'Approve the transaction in Nightly',
      phase: 'send',
      body: [
        'Nightly will show an Approve the transaction popup for Canadawale Yar.',
        'Review the amount, wallet, and network fee, then click Approve to complete the transfer (or Cancel if something looks wrong).',
      ],
      image: {
        src: '/wallet_ui/nightly-approve-transaction.jpg',
        alt: 'Nightly Approve the transaction modal for Canadawale Yar showing SOL network fee',
        caption: 'Approve the Nightly transaction popup to finish sending CWY on Solana.',
      },
      tips: [
        'You will see a similar Approve popup whenever CWY asks Nightly to send a transaction.',
      ],
    },
  ] satisfies GuideStepContent[],
} as const;
